"use client"

import { useMemo, useState } from "react";

import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { copyToClipboard } from "@/lib/utils";
import { useTranslations } from "@/components/i18n/use-translations";

const encodeBase64 = (value: string) => {
    try {
        return btoa(unescape(encodeURIComponent(value)));
    } catch {
        return "";
    }
};

const decodeBase64 = (value: string) => {
    try {
        return decodeURIComponent(escape(atob(value)));
    } catch {
        return "";
    }
};

export function Base64Lab() {
    const [mode, setMode] = useState<"encode" | "decode">("encode");
    const [value, setValue] = useState("https://example.com/path?utm_source=alert");
    const { messages } = useTranslations();

    const result = useMemo(() => (mode === "encode" ? encodeBase64(value) : decodeBase64(value)), [mode, value]);

    return (
        <div className="mx-auto w-full max-w-3xl space-y-6 px-4 py-8">
            <div className="space-y-4 rounded-2xl border border-slate-800 bg-slate-900/70 p-5 shadow-lg shadow-cyan-500/10">
                <div className="flex flex-col gap-2">
                    <p className="text-xs uppercase tracking-[0.2em] text-cyan-200">{messages.tools.base64Lab.label}</p>
                    <h1 className="text-2xl font-semibold text-white">{messages.tools.base64Lab.title}</h1>
                    <p className="text-sm text-gray-400">{messages.tools.base64Lab.description}</p>
                </div>

                <div className="flex gap-2 text-sm">
                    <Button
                        type="button"
                        onClick={() => setMode("encode")}
                        className={`flex-1 h-10 rounded-full text-sm font-semibold transition-all duration-200 ${
                            mode === "encode"
                                ? "bg-linear-to-r from-cyan-500 to-blue-600 text-white shadow-md shadow-cyan-500/25 hover:from-cyan-400 hover:to-blue-500 hover:shadow-cyan-500/35"
                                : "border border-slate-700 bg-slate-800 text-gray-200 hover:border-cyan-500/60"
                        }`}
                    >
                        {messages.tools.base64Lab.encode}
                    </Button>
                    <Button
                        type="button"
                        onClick={() => setMode("decode")}
                        className={`flex-1 h-10 rounded-full text-sm font-semibold transition-all duration-200 ${
                            mode === "decode"
                                ? "bg-linear-to-r from-cyan-500 to-blue-600 text-white shadow-md shadow-cyan-500/25 hover:from-cyan-400 hover:to-blue-500 hover:shadow-cyan-500/35"
                                : "border border-slate-700 bg-slate-800 text-gray-200 hover:border-cyan-500/60"
                        }`}
                    >
                        {messages.tools.base64Lab.decode}
                    </Button>
                </div>

                <div className="space-y-3">
                    <Textarea
                        value={value}
                        onChange={(event) => setValue(event.target.value)}
                        placeholder={messages.tools.base64Lab.placeholder}
                        aria-label={messages.tools.base64Lab.inputLabel}
                        className="min-h-[140px] border border-slate-800 bg-transparent text-slate-100 placeholder:text-gray-500 ring-2 ring-cyan-500/20 focus:ring-cyan-500/45"
                    />
                    <p className="text-xs text-gray-400">{messages.tools.base64Lab.description}</p>
                </div>
            </div>

            <div className="space-y-3 rounded-2xl border border-slate-800 bg-slate-900/70 p-5">
                <div className="flex items-center justify-between">
                    <div>
                        <p className="text-xs uppercase tracking-[0.2em] text-gray-400">{messages.tools.base64Lab.outputLabel}</p>
                        <p className="text-xl font-semibold text-white">
                            {mode === "encode" ? messages.tools.base64Lab.encode : messages.tools.base64Lab.decode} {messages.tools.base64Lab.outputLabel}
                        </p>
                    </div>
                    <Button
                        type="button"
                        className="h-10 rounded-full bg-linear-to-r from-cyan-500 to-blue-600 px-4 text-sm font-semibold text-white shadow-md shadow-cyan-500/25 transition hover:from-cyan-400 hover:to-blue-500 hover:shadow-cyan-500/35"
                        onClick={() => copyToClipboard(result)}
                    >
                        {messages.tools.base64Lab.copy}
                    </Button>
                </div>
                <div className="relative">
                    <Textarea
                        value={result}
                        readOnly
                        aria-label={messages.tools.base64Lab.outputLabel}
                        className="min-h-[140px] border border-cyan-800/50 bg-slate-950/60 text-slate-100 ring-2 ring-cyan-500/15 focus-visible:outline-hidden"
                    />
                    <p className="mt-2 text-xs text-gray-400">
                        {messages.tools.base64Lab.emptyResult}
                    </p>
                </div>
            </div>
        </div>
    );
}
